import { useCallback, useContext, useEffect } from "react";
import ModelElements from "../ModelElements";
import type { ProductModelProps } from "../ModelViewerProduct";
import useGLB, { type HasStandardMaterial } from "../_hooks/useGLB";
import ProductTextureContext from "../_contexts/ProductTextureContext";
import { FindMaterialApprox } from "../../Common/Utils/ThreeTools";

/* Static URLs let webpack discover every model while the active one remains data-driven. */
const MODEL_VARIANTS = {
    'twilly_scarf_flat': new URL('@resources/3d-models/glb/twilly_scarf_flat.glb', import.meta.url),
    'twilly_scarf_model-01': new URL('@resources/3d-models/glb/twilly_scarf_model-01.glb', import.meta.url),
    'twilly_scarf_model-02': new URL('@resources/3d-models/glb/twilly_scarf_model-02.glb', import.meta.url),
} as const

type ScarfModelVariant = keyof typeof MODEL_VARIANTS

const isScarfModelVariant = (model:string|undefined):model is ScarfModelVariant => (
    model !== undefined && Object.prototype.hasOwnProperty.call(MODEL_VARIANTS, model)
)

export default function TwillyScarf(props: ProductModelProps) {
    const fallbackModelVariant:ScarfModelVariant = 'twilly_scarf_flat'
    const modelVariant = isScarfModelVariant(props.model) ? props.model : fallbackModelVariant
    const [productTexture] = useContext(ProductTextureContext)

    const processMaterial = useCallback((object: HasStandardMaterial) => {
        object.material.toneMapped = false
        object.material.depthWrite = true
        object.material.color.set(0xf5f6ee)

        // Just delete the imported alpha map here it doesn't make enough sense for this product
        object.material.map = null
        
        if (object.material.normalMap) {
            object.material.normalScale.x = 3
            object.material.normalScale.y = 3
        }
    }, [])

    const model = useGLB(MODEL_VARIANTS[modelVariant])
    const elements = model.scene.children

    // Tune Materials
    useEffect(() => {
        elements.forEach((e: HasStandardMaterial) => processMaterial(e))
    }, [elements])

    // Apply lab texture
    useEffect(() => {
        let material = FindMaterialApprox('twill_a', elements)
        if (material) {
            material.map = productTexture
            material.map.flipY = false
            material.map.needsUpdate = true
        }

        material = FindMaterialApprox('twill_b', elements)
        if (material) {
            material.map = productTexture
            material.map.flipY = false
            material.map.needsUpdate = true
        }
    }, [productTexture, elements])

    return <ModelElements scene={model.scene} />
}
